import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import { useOrgContext } from '@/hooks/useOrgContext';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableColumnHeader } from '@/components/insurance/SortableColumnHeader';
import { CUSTOMERS_COLUMNS } from '@/lib/tableColumns';
import { cn } from '@/lib/utils';
import { Users, Trash2 } from 'lucide-react';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated is the documented exception

type CustomerRow = {
  // LOCAL_TYPE_JUSTIFIED: narrow row shape used only in this file's renderCustomerCell helper; full Convex return type not exported
  _id: Id<'leads'>;
  name?: string;
  email?: string;
  phoneNumber?: string;
  policyCount: number;
  source?: string;
  createdAt: number;
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function CustomerTableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center px-6 py-3 border-b border-table-row-border gap-4">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-44 ml-4" />
          <Skeleton className="h-4 w-28 ml-4" />
          <Skeleton className="h-4 w-10 ml-4" />
          <Skeleton className="h-4 w-20 ml-4" />
          <Skeleton className="h-4 w-24 ml-auto" />
        </div>
      ))}
    </>
  );
}

function renderCustomerCell(
  colId: string,
  customer: CustomerRow,
  isOrgOwner: boolean,
  onDelete: (e: React.MouseEvent, id: Id<'leads'>) => void
): React.ReactNode {
  switch (colId) {
    case 'name':
      return (
        <span className="text-sm font-medium text-foreground truncate pr-3">
          {customer.name ?? '—'}
        </span>
      );
    case 'email':
      return (
        <span className="text-sm text-muted-foreground truncate pr-3">{customer.email ?? '—'}</span>
      );
    case 'phone':
      return (
        <span className="text-sm text-muted-foreground truncate pr-3">
          {customer.phoneNumber ?? '—'}
        </span>
      );
    case 'policies':
      return <span className="text-sm text-muted-foreground">{customer.policyCount}</span>;
    case 'source':
      return (
        <span className="text-sm text-muted-foreground capitalize truncate pr-3">
          {customer.source ?? '—'}
        </span>
      );
    case 'created':
      return (
        <span className="text-sm text-muted-foreground">{formatDate(customer.createdAt)}</span>
      );
    case 'actions':
      if (!isOrgOwner) return null;
      return (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 w-7"
            onClick={e => onDelete(e, customer._id)}
            aria-label={`Delete ${customer.name ?? 'customer'}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      );
    default:
      return null;
  }
}

export function CustomersListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading, isOrgOwner } = useOrgContext();
  const [searchQuery, setSearchQuery] = useState('');

  const rawCustomers = useQuery(
    api.customers.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const removeCustomer = useMutation(api.customers.remove);

  const { visibleColumns, allColumns, isColumnVisible, toggleColumn, resetToDefaults } =
    useColumnVisibility('insurance-customers', CUSTOMERS_COLUMNS);

  const isLoading = orgLoading || rawCustomers === undefined;

  const filtered = useMemo(() => {
    const list: CustomerRow[] = rawCustomers ?? [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      c =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phoneNumber?.toLowerCase().includes(q)
    );
  }, [rawCustomers, searchQuery]);

  const customerAccessor = useCallback((customer: CustomerRow, colId: string): string | number => {
    switch (colId) {
      case 'name':
        return customer.name ?? '';
      case 'email':
        return customer.email ?? '';
      case 'phone':
        return customer.phoneNumber ?? '';
      case 'policies':
        return customer.policyCount;
      case 'source':
        return customer.source ?? '';
      case 'created':
        return customer.createdAt;
      default:
        return '';
    }
  }, []);

  const {
    sortState,
    toggleSort,
    sortedItems: sortedCustomers,
  } = useTableSort(
    filtered,
    CUSTOMERS_COLUMNS,
    { defaultSort: { sortBy: 'created', direction: 'desc' } },
    customerAccessor
  );

  const handleDelete = (e: React.MouseEvent, id: Id<'leads'>) => {
    e.stopPropagation();
    void removeCustomer({ customerId: id });
  };

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Users className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage customers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Customers</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${rawCustomers?.length ?? 0} total`}
          </p>
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="customer-search" className="sr-only">
            Search customers
          </label>
          <Input
            id="customer-search"
            placeholder="Search by name, email or phone..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <div className="ml-auto">
            <ColumnSettingsPopover
              allColumns={allColumns}
              isColumnVisible={isColumnVisible}
              toggleColumn={toggleColumn}
              resetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {/* Table header */}
          <div className="flex items-center min-w-max px-6 py-2 border-b border-table-row-border bg-muted/30">
            {visibleColumns.map(col => (
              <div
                key={col.id}
                className={cn(
                  'shrink-0',
                  col.width,
                  col.pinned === 'left' && 'sticky left-0 z-10 bg-muted/30',
                  col.pinned === 'right' && 'sticky right-0 z-10 bg-muted/30 text-right'
                )}
              >
                <SortableColumnHeader
                  column={col}
                  sortState={sortState}
                  onToggleSort={toggleSort}
                />
              </div>
            ))}
          </div>

          <div className="overflow-auto">
            {isLoading && <CustomerTableSkeleton />}

            {!isLoading &&
              sortedCustomers.map(customer => (
                <div
                  key={customer._id}
                  className="flex items-center min-w-max px-6 py-3 border-b border-table-row-border hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate(`/customers/${customer._id}`)}
                >
                  {visibleColumns.map(col => (
                    <span
                      key={col.id}
                      className={cn(
                        'shrink-0 text-sm pr-3',
                        col.width,
                        col.pinned === 'left' && 'sticky left-0 z-10 bg-background',
                        col.pinned === 'right' && 'sticky right-0 z-10 bg-background'
                      )}
                    >
                      {renderCustomerCell(col.id, customer, isOrgOwner, handleDelete)}
                    </span>
                  ))}
                </div>
              ))}

            {!isLoading && filtered.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Users className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-1">No customers found</h3>
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? 'Try adjusting your search query'
                    : 'Customers will appear here once leads are converted'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
